import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { CucmHuntList, CucmLineGroupMembership, JobState } from '../../../shared/models/generated/ziroModels';
import { TranslateService } from '@ngx-translate/core';
import { MigrationsCucmHuntListsContext } from '../../services/cisco/migrations-cucm-hunt-lists.context';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { Subscription } from 'rxjs';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';

interface HuntListDatatableRow extends DatatableRow {
  name: string;
  description: string;
  huntPilots: string[];
  lineGroups: CucmLineGroupMembership[];
  totalIncomingCalls: number;
  numMembers: number;
  memberships: CucmLineGroupMembership[];
}

enum DistributionAlgorithmType {
  TOP_DOWN = 'Top Down',
  BROADCAST = 'Broadcast',
  LONGEST_IDLE_TIME = 'Longest Idle Time',
  CIRCULAR = 'Circular',
}

@Component({
  selector: 'ziro-migrations-cisco-hunt-lists-report',
  templateUrl: 'migrations-cisco-hunt-lists-report.component.html',
  styleUrls: ['migrations-cisco-hunt-lists-report.component.scss', '../../migrations.component.scss'],
  standalone: false,
})
export class MigrationsCiscoHuntListsReportComponent implements OnInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _migrationCucmHuntListsContext = inject(MigrationsCucmHuntListsContext);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _lineGroupDistributionIcons = new Map<DistributionAlgorithmType, ZiroIcons>([
    [DistributionAlgorithmType.TOP_DOWN, ZiroIcons.DISTRIBUTION_TOP_DOWN],
    [DistributionAlgorithmType.CIRCULAR, ZiroIcons.DISTRIBUTION_CIRCULAR],
    [DistributionAlgorithmType.LONGEST_IDLE_TIME, ZiroIcons.DISTRIBUTION_LONGEST_IDLE_TIME],
    [DistributionAlgorithmType.BROADCAST, ZiroIcons.DISTRIBUTION_BROADCAST],
  ]);

  isLoading = true;

  columns: DatatableColumn<HuntListDatatableRow>[] = [
    {
      name: 'name',
      label: 'tkey;migrations.cisco.hunt_lists.list_view.table.name',
    },
    {
      name: 'description',
      label: 'tkey;migrations.cisco.hunt_lists.list_view.table.description',
      columnCharacterWidth: 50,
    },
    {
      name: 'lineGroups',
      label: 'tkey;migrations.cisco.hunt_lists.list_view.table.line_groups',
      columnCharacterWidth: 30,
      sortFn: (a: CucmLineGroupMembership[], b: CucmLineGroupMembership[]) =>
        (a[0]?.lineGroupName || '').localeCompare(b[0]?.lineGroupName || ''),
    },
    {
      name: 'huntPilots',
      label: 'tkey;migrations.cisco.hunt_lists.list_view.table.pilots',
      columnCharacterWidth: 20,
      sortFn: (a: string[], b: string[]) => (a[0] || '').localeCompare(b[0] || ''),
    },
    {
      name: 'numMembers',
      label: 'tkey;migrations.cisco.hunt_lists.list_view.table.memberships',
      columnCharacterWidth: 15,
      pipe: {
        transform: (num: number): string =>
          this._translateService.instant('tkey;migrations.cisco.hunt_lists.list_view.table.memberships.count', {
            num,
          }),
      },
    },
  ];
  tableRows: HuntListDatatableRow[] = [];

  lineGroupsPopoverData: CucmLineGroupMembership[];
  private _assessmentId: string;
  private _tenantId: string;
  private _subscriptions = new Subscription();
  private isCdrPresent = false;

  @ViewChild('lineGroupsPopover', { static: true }) lineGroupsPopoverTemplate!: TemplateRef<any>;
  ngOnInit(): void {
    this._initBottomNav();
    this._assessmentId = this._route.snapshot.params['id'];
    this._tenantId = this._route.snapshot.params['tenantId'];

    const exportSub = this._migrationsAssessmentsContext.state$.subscribe((migrations) => {
      const assessment = migrations.ciscoPbxExports.find((migration) => migration.id === this._assessmentId);
      this.isCdrPresent = assessment.isCucmCdrDumpPresent;
      if (this.isCdrPresent) {
        this.columns.splice(4, 0, {
          name: 'totalIncomingCalls',
          label: 'tkey;migrations.cisco.hunt_lists.list_view.table.incoming_calls',
          columnCharacterWidth: 20,
        });
      }
      this._initBreadcrumbs(assessment.name);
    });
    this._subscriptions.add(exportSub);

    const huntListSub = this._migrationCucmHuntListsContext.state$.subscribe((report) => {
      this.tableRows = this._mapReportToRows(report);
      this.isLoading = false;
    });
    this._subscriptions.add(huntListSub);

    const huntListStatusSub = this._migrationCucmHuntListsContext.jobState$.subscribe((status) => {
      if (status.lastRunTime) {
        this._updateBottomNavTimestamp(status.lastRunTime);
      }
      const isRefreshing = !status.lastRunTime || status.jobState !== JobState.IDLE;
      this._setRefreshButtonPendingState(isRefreshing);
    });
    this._subscriptions.add(huntListStatusSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  lineGroupMembershipSearch = (searchValue: string, row: HuntListDatatableRow): boolean => {
    return row.memberships.some(
      (membership) =>
        membership.lineGroupName.toLowerCase().includes(searchValue.toLowerCase()) ||
        membership.lineGroupMembers.some((member) => member.extension.includes(searchValue)),
    );
  };

  lineGroupDistributionIcon = (lg: CucmLineGroupMembership): ZiroIcons =>
    this._lineGroupDistributionIcons.get(lg.distributionAlgorithm as DistributionAlgorithmType);

  private _mapReportToRows(report: CucmHuntList[]): HuntListDatatableRow[] {
    return report.map((huntList) => {
      const orderedHuntList = this._orderBySelectionOrder(huntList.lineGroupMemberships);

      return {
        name: huntList.name,
        description: huntList.description,
        huntPilots: huntList.huntPilots.map((pilot) => pilot.huntPilotNumber),
        totalIncomingCalls: huntList.huntPilots.reduce((sum, huntPilot) => sum + huntPilot.cdrCount, 0),
        numMembers: this._getNumberOfMembers(huntList.lineGroupMemberships),
        memberships: huntList.lineGroupMemberships,
        lineGroups: huntList.lineGroupMemberships,
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            icon: ZiroIcons.NEXT,
            size: ButtonSizes.XSMALL,
            dataAutomation: 'migrations-cisco-hunt-lists-datatable-view-line-groups-button',
            onClick: (row) => this._router.navigate([row.name], { relativeTo: this._route }),
          },
        ],
        badges: {
          huntPilots: {
            values:
              huntList.huntPilots.length >= 1
                ? huntList.huntPilots.map((pilot) => {
                    return {
                      label: this._telephoneNumberFilter.transform(pilot.huntPilotNumber),
                    };
                  })
                : [
                    {
                      label: 'N/A',
                      icon: ZiroIcons.WARNING_TRIANGLE,
                      cssClass: 'bg-warning',
                      tooltip: 'tkey;migrations.cisco.hunt_lists.line_groups.hunt_pilots.no_pilot_warning',
                    },
                  ],
          },
          lineGroups: {
            maxLength: 30,
            values:
              huntList.lineGroupMemberships.length === 1
                ? [
                    {
                      label: huntList.lineGroupMemberships[0].lineGroupName,
                      icon: this.lineGroupDistributionIcon(huntList.lineGroupMemberships[0]),
                      cssClass: `alert alert-inline alert-info`,
                      tooltip: huntList.lineGroupMemberships[0].distributionAlgorithm,
                    },
                  ]
                : huntList.lineGroupMemberships.length > 1
                  ? [
                      {
                        label: orderedHuntList[0].lineGroupName,
                        icon: this.lineGroupDistributionIcon(orderedHuntList[0]),
                        cssClass: `alert alert-inline alert-info`,
                        tooltip: orderedHuntList[0].distributionAlgorithm,
                        popover: {
                          popoverTemplate: () => this.lineGroupsPopoverTemplate,
                          popoverClass: 'line-group-popover',
                          popoverHtml: `<i class="${ZiroIcons.DISTRIBUTION_DROP_DOWN} cursor-pointer"></i>`,
                          onClick: (row: HuntListDatatableRow) => this._onClickMoreLineGroups(row),
                        },
                      },
                    ]
                  : [],
          },
        },
      };
    });
  }

  private _orderBySelectionOrder(lineGroups: CucmLineGroupMembership[]): CucmLineGroupMembership[] {
    return [...lineGroups].sort((a, b) => a.selectionOrder - b.selectionOrder);
  }

  private _getNumberOfMembers(lineGroupMemberships: CucmLineGroupMembership[]): number {
    const extensions = lineGroupMemberships.flatMap((membership) =>
      membership.lineGroupMembers.map((member) => `${member.extension}_${member.routePartition}`),
    );
    return new Set(extensions).size;
  }

  private _onRefreshClick(): void {
    this.isLoading = true;
    this._migrationCucmHuntListsContext.refresh().subscribe();
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'cucm-hunt-lists-report-refresh',
          dataAutomation: 'migrations-cisco-hunt-lists-refresh-button',
          label: 'tkey;migrations.cisco.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.DEFAULT,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this._onRefreshClick(),
        },
        {
          id: 'cucm-hunt-lists-report-back',
          dataAutomation: 'migrations-cisco-hunt-lists-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => {
            this._router.navigate(['..'], { relativeTo: this._route });
          },
        },
      ]),
    );
  }

  private _updateBottomNavTimestamp(timestamp: string): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${ZiroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.dial_plan_inventory.last_updated',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _setRefreshButtonPendingState(state: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'cucm-hunt-lists-report-refresh',
        state: {
          pending: state,
          buttonDisableState: { disabled: state, tooltipKey: '' },
        },
      }),
    );
  }

  private _initBreadcrumbs(migrationName: string): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: migrationName,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this._assessmentId}`,
      },
      { label: 'tkey;migrations.cisco.reports_list.table.hunt_lists' },
    ]);
  }

  private _onClickMoreLineGroups(row: HuntListDatatableRow): void {
    this.lineGroupsPopoverData = this._orderBySelectionOrder(row.lineGroups);
  }
}
